import { formatCurrency } from '@/shared/lib/format';

import type { PricingStepValue } from '../types/pricing.types';

/*
 * Display only: formats values the pricing engine produced. Nothing here adds,
 * multiplies or rounds money.
 */

const LOCALE = 'en-GB';

/** An engine amount at the precision the engine produced; plain numbers (rates) without a currency. */
export function formatEngineValue(
  value: number | null | undefined,
  currency?: string | null,
): string {
  if (currency) return formatCurrency(value, currency, { precise: true });
  if (value === null || value === undefined) return '—';
  return new Intl.NumberFormat(LOCALE, { maximumFractionDigits: 8 }).format(value);
}

/** 0.06 → "6%" (display of a stored fraction). */
export function formatFraction(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  return new Intl.NumberFormat(LOCALE, { style: 'percent', maximumFractionDigits: 3 }).format(
    value,
  );
}

/*
 * Percentages are stored as fractions (0.05 = 5%) but staff read and type them
 * as percents. Rounding to the stored precision (decimal(18,6) as a fraction)
 * drops binary noise such as 0.07 × 100 = 7.000000000000001.
 */

/** 0.05 → 5, for an input staff type a percent into. */
export function fractionToPercent(fraction: number): number {
  return Number((fraction * 100).toFixed(4));
}

/** 5 → 0.05, the fraction the API stores. */
export function percentToFraction(percent: number): number {
  return Number((percent / 100).toFixed(6));
}

/** A fraction exactly as it is stored: 0.05 → "0.05", shown beside its percent. */
export function formatStoredFraction(value: number): string {
  return new Intl.NumberFormat(LOCALE, { maximumFractionDigits: 6 }).format(value);
}

/** A step value as its kind says to show it. */
export function formatStepValue(value: PricingStepValue): string {
  if (value.kind === 'Fraction') return formatFraction(value.value);
  if (value.kind === 'Money') return formatEngineValue(value.value, value.currency ?? null);
  return formatEngineValue(value.value);
}
